import React from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { useTouchLayout } from "../../hooks/useTouchLayout";

interface MenuRowProps {
  icon?: React.ReactNode;
  label: string;
  /** Secondary line, e.g. "12 stocks". */
  detail?: string;
  selected?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  /** Why the row is unavailable. Tooltip on pointer, inline text on touch. */
  disabledReason?: string;
  trailing?: React.ReactNode;
  onClick?: () => void;
}

/**
 * One tappable row inside an AdaptivePopover menu or bottom sheet.
 *
 * The house select-menu idiom (FinancialPeriodSelector's items): 40px on
 * pointer, 44px on touch, radius 1, 0.875rem. The chosen row carries a green
 * outline and a faint green wash rather than a solid fill (Ben, 2026-09-17).
 * The house press treatment (scale + tint, no tap highlight) so it reads as
 * native on touch. Disabled rows explain themselves inline on touch, because
 * tooltips never fire there.
 */
const MenuRow: React.FC<MenuRowProps> = ({
  icon,
  label,
  detail,
  selected = false,
  destructive = false,
  disabled = false,
  disabledReason,
  trailing,
  onClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  const labelColour = () => {
    if (disabled) return theme.palette.text.disabled;
    if (destructive) return theme.palette.error.main;
    if (selected) return "#22c55e";
    return isDark ? "#ffffff" : "#1d1d1f";
  };

  const row = (
    <Box
      role="menuitem"
      aria-disabled={disabled}
      aria-current={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onClick?.()}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minHeight: isTouch ? 44 : 40,
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        // A transparent border on every row so the selected outline never
        // shifts the layout.
        border: `1px solid ${selected ? "rgba(34, 197, 94, 0.45)" : "transparent"}`,
        cursor: disabled ? "default" : "pointer",
        backgroundColor: selected ? "rgba(34, 197, 94, 0.08)" : "transparent",
        WebkitTapHighlightColor: "transparent",
        transition:
          "transform 0.1s ease, background-color 0.1s ease, border-color 0.1s ease",
        "&:focus-visible": {
          outline: `2px solid ${destructive ? theme.palette.error.main : "#22c55e"}`,
          outlineOffset: -2,
        },
        ...(!disabled && {
          "&:hover": {
            backgroundColor: selected
              ? "rgba(34, 197, 94, 0.12)"
              : isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.04)",
          },
          "&:active": {
            transform: "scale(0.98)",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
          },
        }),
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:active": { transform: "none" },
        },
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            color: disabled ? theme.palette.text.disabled : labelColour(),
          }}
        >
          {icon}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: "0.875rem",
            fontWeight: selected ? 600 : 500,
            lineHeight: 1.3,
            color: labelColour(),
          }}
        >
          {label}
        </Typography>
        {detail && (
          <Typography noWrap sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
            {detail}
          </Typography>
        )}
        {disabled && disabledReason && isTouch && (
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
            {disabledReason}
          </Typography>
        )}
      </Box>

      {trailing && <Box sx={{ display: "flex", flexShrink: 0 }}>{trailing}</Box>}
    </Box>
  );

  if (disabled && disabledReason && !isTouch) {
    return (
      <Tooltip title={disabledReason} placement="left">
        {/* Disabled rows swallow pointer events, so the span carries the hover */}
        <span>{row}</span>
      </Tooltip>
    );
  }

  return row;
};

export default MenuRow;
